import { zodResolver } from "@hookform/resolvers/zod";
import { MouseEvent, MouseEventHandler, use, useEffect, useState } from "react";
import { Resolver, useForm, useWatch } from "react-hook-form";
import {
  CardsListItem,
  CreateCardRequest,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { normalizeEmail } from "tng-address-collection-shared/types/normalizations.ts";
import { fallbackCard } from "../defaults/fallbackCard.tsx";
import { useCheckEmail } from "../hooks/useCheckEmail.ts";
import { useCreateCard } from "../hooks/useCreateCard.ts";
import { useDebouncedValue } from "../hooks/useDebouncedValue.ts";
import { useGetCreateCardExampleData } from "../hooks/useGetCreateCardExampleData.ts";
import { useShareCard } from "../hooks/useShareCard.ts";
import { useTranslator } from "../i18n/translator.ts";
import { SnackbarContext } from "../snackbar/SnackbarContext.ts";
import { CardFormFields, EmailDuplicateState } from "./CardFormFields.tsx";
import { FormContainer } from "./FormContainer.tsx";

interface CreateCardDialogProps {
  onClose: () => void;
  projectId: string;
  cards: CardsListItem[];
}

export const CreateCardDialog = ({
  projectId,
  onClose,
  cards,
}: CreateCardDialogProps) => {
  const defaultCard = useGetCreateCardExampleData(projectId);
  const { displayError } = use(SnackbarContext);

  const translate = useTranslator();
  const { mutate: createCard, isPending: isCreatePending } = useCreateCard();

  const { control, handleSubmit, reset, resetField, register, setFocus } =
    useForm({
      resolver: zodResolver(CreateCardRequest) as Resolver<CreateCardRequest>,
      defaultValues: defaultCard ?? fallbackCard,
    });

  const emailValue = useWatch({ control, name: "recipient.email" }) ?? "";
  const debouncedEmail = useDebouncedValue(emailValue, 750);

  // The uniqueness check reads an eventually consistent projection and can
  // still report a just-saved email as unique.
  const [savedEmails, setSavedEmails] = useState<Set<string>>(new Set());

  const {
    cardInOtherProject,
    isUnique,
    isLoading: isCheckingEmail,
  } = useCheckEmail(projectId, debouncedEmail);
  const { mutate: shareCard, isPending: isSharing } = useShareCard();

  useEffect(() => {
    reset(defaultCard, { keepDirtyValues: true });
  }, [defaultCard, reset]);

  const onSubmitCreate = (formData: CreateCardRequest) => {
    createCard(formData, {
      onSuccess: () => {
        if (formData.recipient.email) {
          const normalizedEmail = normalizeEmail(formData.recipient.email);
          setSavedEmails((previous) => new Set(previous).add(normalizedEmail));
        }
        reset({
          projectId: projectId,
          recipient: {
            title: "",
            firstName: "",
            lastName: "",
            email: "",
            salutationKind: defaultCard?.recipient.salutationKind ?? "male",
            preferredLanguage: formData.recipient.preferredLanguage,
            companyName: formData.recipient.companyName,
          },
          senderId: formData.senderId,
          comment: "",
          departmentName: formData.departmentName,
          manualDispatch: formData.manualDispatch,
          receivesGift: formData.receivesGift,
          formality: formData.formality,
        } satisfies typeof formData);
        setFocus("recipient.firstName");
      },
    });
  };

  const onSubmitShare = (e?: MouseEvent) => {
    e?.preventDefault();
    if (cardInOtherProject) {
      shareCard(
        {
          sharedProjectId: projectId,
          cardId: cardInOtherProject.cardId,
          responsibleProjectId: cardInOtherProject.responsibleProjectId,
        },
        {
          onSuccess: () => {
            reset({
              ...defaultCard,
              recipient: {
                ...defaultCard?.recipient,
                email: "",
                firstName: "",
                lastName: "",
              },
            });
            setFocus("recipient.email");
          },
        },
      );
    }
  };

  const isCheckingCurrentEmail =
    emailValue !== debouncedEmail || isCheckingEmail;
  const emailDuplicateState: EmailDuplicateState = savedEmails.has(
    normalizeEmail(emailValue),
  )
    ? { status: "duplicate-blocked" }
    : isCheckingCurrentEmail
      ? { status: "checking" }
      : !isUnique
        ? !cardInOtherProject
          ? { status: "duplicate-blocked" }
          : { status: "duplicate-shareable", isSharing, onShare: onSubmitShare }
        : { status: "unique" };

  useEffect(() => {
    if (emailDuplicateState.status === "duplicate-blocked") {
      displayError(translate("errorCardEmailNotUnique"));
    }
  }, [emailDuplicateState.status, displayError, translate]);

  return (
    <FormContainer
      title={translate("createCardTitle")}
      onClose={onClose}
      isLoading={!defaultCard}
      saveConfig={{
        onSave: handleSubmit(onSubmitCreate) as MouseEventHandler,
        isSaveDisabled:
          isCreatePending ||
          emailDuplicateState.status === "duplicate-blocked" ||
          emailDuplicateState.status === "duplicate-shareable",
        saveButtonText: translate("saveAndNew"),
      }}
    >
      <CardFormFields
        control={control}
        resetField={resetField}
        register={register}
        projectId={projectId}
        cards={cards}
        emailDuplicateState={emailDuplicateState}
        responsibleProjectId={cardInOtherProject?.responsibleProjectId}
      />
    </FormContainer>
  );
};
